import InviteUserIcon from "@/assets/colorable/invite_user.svg?react";
import EditIcon from "@/assets/colorable/pen_edit.svg?react";
import PendingIcon from "@/assets/colorable/pending.svg?react";
import CrossIcon from "@/assets/colorable/times.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import Button from "@/components/Common/Button";
import UpdateInvitePermission from "@/components/MultiAccount/UpdateInvitePermission";
import UpdateUserPermission from "@/components/MultiAccount/UpdateUserPermission";
import UserNotificationToggle from "@/components/MultiAccount/UserNotificationToggle";
import type {SubBrandInviteSerializer} from "@/generated/Company";
import CompanyLogoPlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import clsxm from "@/lib/clsxm";
import {AbilityContext, Can} from "@/permissions";
import {
  editCompanyMemberModalAtom,
  isAgencyInviteModalOpenAtom,
  isRequestPermissionsModalOpenAtom,
  multiAccountInviteModalAtom,
  selectedCompanyIdAtom,
} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TAccountMember} from "@/store/types";
import useUserStore from "@/store/user";
import {getUserPermission} from "@/utils/permissionUtils";
import {useSetAtom} from "jotai";
import {useContext, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Tooltip} from "react-tooltip";

const CompanyMembers: FC<{
  companyId: number;
  companyMembers: TAccountMember[];
  companyInvites: SubBrandInviteSerializer[];
  upgradePermission(memberId: number): void;
  isCompanyEdit?: boolean;
  className?: string;
  isAgency?: boolean;
  isSubbrand?: boolean;
}> = ({
  companyId,
  companyMembers,
  companyInvites,
  upgradePermission,
  isCompanyEdit = false,
  className,
  isAgency = false,
  isSubbrand = false,
}) => {
  const {t} = useTranslation("multi_account");
  const abilityContext = useContext(AbilityContext);
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const setInviteModal = useSetAtom(multiAccountInviteModalAtom);
  const setAgencyInviteModal = useSetAtom(isAgencyInviteModalOpenAtom);
  const setEditCompanyMemberModal = useSetAtom(editCompanyMemberModalAtom);
  const setIsRequestPermissionsModalVisible = useSetAtom(isRequestPermissionsModalOpenAtom);
  const loggedInUser = useUserStore(state => state.user);
  const setRegistrationValue = useRegistrationStore(state => state.setValue);

  return (
    <div className={clsxm("flex flex-col gap-5", className)}>
      {companyMembers?.map(member => {
        const isViewingSelf = member.user.id === Number(loggedInUser.id);
        const canUpdateMemberList = abilityContext.can("update", "CompanyMembers");
        const canUpdateMember = abilityContext.can("update", "CompanyMember");
        const hasPermissionRequest = member.permission_request?.status === "PENDING";
        const shouldShowPermissionRequestWarning = hasPermissionRequest && (isViewingSelf || canUpdateMember);

        return (
          <div key={member.id} className="flex flex-col gap-2">
            <div key={member.id} className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
                  <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
                    <img
                      src={member.user.profile_picture ?? CompanyLogoPlaceholder}
                      className="h-full w-full shrink-0 rounded-full object-fill"
                    />
                  </div>
                  <span
                    onClick={() => {
                      navigator.clipboard.writeText(member.user.email!);
                      toast.success(t("email_copied"));
                    }}
                    data-tip
                    data-tooltip-id={`member-${member.id}-email`}
                    className="cursor-copy bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                    {member.user.first_name} {member.user.last_name}
                  </span>
                  <Tooltip
                    id={`member-${member.id}-email`}
                    place="top"
                    variant="dark"
                    delayShow={100}
                    className="z-50 opacity-100">
                    {t("email")}: {member.user.email}
                  </Tooltip>
                </div>
                {!isAgency && isCompanyEdit && isViewingSelf && !hasPermissionRequest && (
                  <Can not I="create" a="Campaign">
                    <Button
                      buttonStyle="Blue"
                      className="hidden h-auto px-4 py-1 sm:flex"
                      onClick={() => setIsRequestPermissionsModalVisible(true)}>
                      {t("request_more_permissions")}
                    </Button>
                  </Can>
                )}
              </div>
              {isAgency && member?.owner && (
                <div className="justify-end text-xs font-medium">{t("admin_permissions")}</div>
              )}
              {!isAgency && (
                <div className="flex items-center gap-3">
                  {isViewingSelf && isCompanyEdit && (
                    <Button
                      buttonStyle="White"
                      className="h-auto rounded-full"
                      onClick={() => {
                        setRegistrationValue("isEmailPrefilled", true);
                        setRegistrationValue("email", loggedInUser.email!);
                        setRegistrationValue("firstName", loggedInUser.first_name!);
                        setRegistrationValue("lastName", loggedInUser.last_name!);

                        setEditCompanyMemberModal({
                          isVisible: true,
                          isRegistering: false,
                        });
                      }}>
                      <EditIcon className="size-3 shrink-0" />
                    </Button>
                  )}
                  {getUserPermission(member) === "ADMIN" || isSubbrand || (!canUpdateMemberList && !isViewingSelf) ? (
                    <div className="flex items-center gap-3">
                      <div className="border-primary-light-solid text-neutral-black h-8 rounded-md border px-4 py-2 text-xs">
                        {t(`${getUserPermission(member).toLowerCase()}_permissions`)}
                      </div>
                      <UserNotificationToggle
                        userId={member.user.id!}
                        isInitiallyEnabled={!!member.user.email_notification_preferences?.email_enabled}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {shouldShowPermissionRequestWarning && (
                        <div className="border-warning-sand bg-warning-light-yellow text-warning-dark-yellow hidden min-h-8 items-center gap-4 rounded-md border px-2 py-1 text-sm font-semibold lg:flex">
                          <div className="flex items-center gap-2">
                            <WarningIcon className="text-warning-medium-yellow size-[14px] shrink-0" />
                            <span className="inline-block max-w-[200px]">
                              {t("requesting_permission", {
                                permission: t(
                                  `${member.permission_request?.permission_type?.toLowerCase()}_permissions`,
                                ),
                              })}
                            </span>
                          </div>
                          <Can I="update" a="CompanyMember">
                            <button
                              onClick={() => upgradePermission(member.user.id!)}
                              className="text-neutral-grey-dark hover:text-neutral-grey-light">
                              <CrossIcon className="size-2 shrink-0" />
                            </button>
                          </Can>
                        </div>
                      )}
                      <UpdateUserPermission
                        companyMember={member}
                        permission={getUserPermission(member)}
                        isCompanyEdit={isCompanyEdit}
                      />
                      <UserNotificationToggle
                        userId={member.user.id!}
                        isInitiallyEnabled={!!member.user.email_notification_preferences?.email_enabled}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {!isAgency && (
              <div className="flex items-center gap-3 lg:hidden">
                {isCompanyEdit && isViewingSelf && !hasPermissionRequest && (
                  <Can not I="create" a="Campaign">
                    <Button
                      buttonStyle="Blue"
                      className="h-auto px-4 py-1 sm:hidden"
                      onClick={() => setIsRequestPermissionsModalVisible(true)}>
                      {t("request_more_permissions")}
                    </Button>
                  </Can>
                )}
                {shouldShowPermissionRequestWarning && (
                  <div className="border-warning-sand bg-warning-light-yellow text-warning-dark-yellow flex min-h-8 items-center gap-4 rounded-md border px-2 py-1 text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <WarningIcon className="text-warning-medium-yellow size-[14px] shrink-0" />
                      {t("requesting_permission", {
                        permission: t(`${member.permission_request?.permission_type?.toLowerCase()}_permissions`),
                      })}
                    </div>
                    <Can I="update" a="CompanyMember">
                      <button
                        onClick={() => upgradePermission(member.user.id!)}
                        className="text-neutral-grey-dark hover:text-neutral-grey-light">
                        <CrossIcon className="size-2 shrink-0" />
                      </button>
                    </Can>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {companyInvites.map(invite => (
        <div key={invite.id} className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="border-neutral-grey-light-dark flex max-w-[45vw] min-w-20 items-center gap-2 rounded-full border border-dashed p-1 pr-2 sm:max-w-[auto]">
            <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
              <img src={CompanyLogoPlaceholder} className="h-full w-full shrink-0 rounded-full object-fill" />
            </div>
            <span
              onClick={() => {
                navigator.clipboard.writeText(invite.email!);
                toast.success(t("email_copied"));
              }}
              data-tip
              data-tooltip-id={`invite-${invite.id}-email`}
              className="cursor-copy truncate bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold break-all text-transparent">
              {invite.email}
            </span>
            <Tooltip
              id={`invite-${invite.id}-email`}
              place="top"
              variant="dark"
              delayShow={100}
              className="z-50 opacity-100">
              {t("pending_invitation_to")}: <br />
              {invite.email}
            </Tooltip>
          </div>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
            <div className="border-primary-light-solid flex items-center gap-2 rounded-md border p-2">
              <PendingIcon className="text-neutral-grey-dark" />
              <span className="text-neutral-grey-dark hidden text-xs sm:inline">{t("pending")}</span>
            </div>
            {!isAgency && (
              <>
                <Can I="update" a="CompanyInvite">
                  <UpdateInvitePermission
                    companyId={companyId}
                    invite={invite}
                    permission={invite.permission!}
                    isCompanyEdit={isCompanyEdit}
                  />
                </Can>
                <Can not I="update" a="CompanyInvite">
                  <div className="border-primary-light-solid text-neutral-black h-8 rounded-md border px-4 py-2 text-xs text-nowrap">
                    {t(`${invite.permission!.toLowerCase()}_permissions`)}
                  </div>
                </Can>
              </>
            )}
          </div>
        </div>
      ))}
      {isCompanyEdit && !isSubbrand && (
        <Can I="create" a="CompanyInvite">
          <Button
            onClick={() => {
              setSelectedCompanyId(companyId);

              if (isAgency) {
                setAgencyInviteModal(true);
              } else {
                setInviteModal({
                  isVisible: true,
                  isViewingOwnCompany: false,
                });
              }
            }}
            buttonStyle="Blue"
            className="h-8 gap-2.5">
            <InviteUserIcon className="size-4 shrink-0" />
            {t("invite_another_user")}
          </Button>
        </Can>
      )}
    </div>
  );
};

export default CompanyMembers;
